import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Mail,
  Maximize2,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react'
import Handlebars from 'handlebars'
import { useQueryClient } from '@tanstack/react-query'
import { useMailTemplateById } from '@data/mailTemplates/mailTemplates'
import { TicketFormData } from '@components/main/Tickets/TicketDetails'


interface TemplatePreviewProps {
  templateId: string;
  deviceName?: string;
  deviceId?: string;
  onValidationChange?: (isValid: boolean) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onCancel?: () => void;
  setWasCanceled: Dispatch<SetStateAction<boolean>>;
  wasCanceled: boolean;
  onRefetchReady?: (refetch: () => void) => void;
  formState:  [TicketFormData, React.Dispatch<React.SetStateAction<TicketFormData>>]
}

export const TemplatePreview = ({
                                  templateId,
                                  deviceName,
                                  deviceId,
                                  onValidationChange,
                                  onLoadingChange,
                                  onCancel,
                                  setWasCanceled,
                                  wasCanceled,
                                  onRefetchReady,
                                  formState
                                }: TemplatePreviewProps) => {
  // const [formData, setFormData] = useState<TicketFormData>({
  //   device_name: deviceName || '',
  //   contact_name: '',
  //   additional_info_pre: '',
  //   additional_info_post: '',
  // })

  const [errors, setErrors] = useState({
    device_name: false,
    device_id: false,
    contact_name: false,
  })
  const [formData, setFormData] = formState
  const [isExpanded, setIsExpanded] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isTemplateInfoExpanded, setIsTemplateInfoExpanded] = useState(false)
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false)

  const {
    data: template,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    // error,
    refetch,
  } = useMailTemplateById(templateId)
  const queryClient = useQueryClient()

  // Function to cancel the template fetch
  const handleCancelFetch = () => {
    setWasCanceled(true)
    queryClient.cancelQueries({ queryKey: ['mailTemplate', templateId] })
  }

  // Reset wasCanceled when templateId changes
  useEffect(() => {
    setWasCanceled(false)
  }, [templateId])

  // Pass refetch function to parent
  useEffect(() => {
    if (onRefetchReady && refetch) {
      onRefetchReady(() => refetch())
    }
  }, [refetch, onRefetchReady])

  // Update device_name when prop changes
  useEffect(() => {
    setFormData(prev => {
      const nextDeviceName = deviceName ?? prev.device_name
      const nextDeviceId = deviceId ?? prev.device_id
      const nextSha256 = template?.content.sha256 ?? prev.sha256

      if (
        prev.device_name === nextDeviceName &&
        prev.device_id === nextDeviceId &&
        prev.sha256 === nextSha256
      ) {
        return prev
      }

      return {
        ...prev,
        device_name: nextDeviceName,
        device_id: nextDeviceId,
        sha256: nextSha256,
      }
    })
  }, [deviceName, deviceId, template, setFormData])

  const compiledTemplate = useMemo(() => {
    if (!template) return null

    try {
      return {
        subject: Handlebars.compile(template.content.subject),
        plainContent: Handlebars.compile(template.content.plainContent),
        htmlContent: Handlebars.compile(template.content.htmlContent),
      }
    } catch (error) {
      console.error('Error compiling template:', error)
      return null
    }
  }, [template])

  // Render the compiled template against current form data
  const renderedTemplate = useMemo(() => {
    if (isLoading || !compiledTemplate) return null

    try {
      return {
        subject: compiledTemplate.subject(formData),
        plainContent: compiledTemplate.plainContent(formData),
        htmlContent: compiledTemplate.htmlContent(formData),
      }
    } catch (error) {
      console.error('Error rendering template:', error)
      return null
    }
  }, [compiledTemplate, formData, isLoading])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing in required field
    if (field === 'device_name' || field === 'contact_name') {
      if (value.trim()) {
        setErrors(prev => ({ ...prev, [field]: false }))
      }
    }
  }

  const isFormValid = formData.device_name.trim() && formData.contact_name.trim()

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(!!isFormValid)
    }
  }, [isFormValid, onValidationChange])

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading || isFetching)
    }
  }, [isLoading, isFetching, onLoadingChange])


  // Handle loading state
  if (isLoading || isFetching || isRefetching) {
    return (
      <Alert icon={<Loader size={16} />} title='Loading template' color='blue'>
        <Stack gap='sm'>
          <Group gap='xs' align='center'>
            <Loader size='sm' type='dots' />
            <Text size='sm'>Please wait while we load the template...</Text>
          </Group>
          <Button
            variant='subtle'
            size='xs'
            color='gray'
            leftSection={<X size={14} />}
            onClick={handleCancelFetch}
          >
            Cancel Request
          </Button>
        </Stack>
      </Alert>
    )
  }

  // Handle canceled state
  if (wasCanceled && !template && !isLoading) {
    return (
      <Alert icon={<XCircle size={16} />} title='Request canceled' color='orange'>
        <Stack gap='sm'>
          <Text size='sm'>
            The request to download the template was canceled by the user.
          </Text>
          {onCancel && (
            <Button
              variant='subtle'
              size='xs'
              color='blue'
              leftSection={<RefreshCw size={14} />}
              onClick={() => {
                setWasCanceled(false)
                // onCancel()
                refetch()
              }}
            >
              Retry Loading Template
            </Button>
          )}
        </Stack>
      </Alert>
    )
  }

  // Handle error state
  if (isError) {
    return (
      <Alert icon={<XCircle size={16} />} title='Error loading template' color='red'>
        There was an error loading the template. Please try selecting a different template or refresh the page.
      </Alert>
    )
  }

  // Handle no template state
  if (!template) {
    return (
      <Alert icon={<AlertCircle size={16} />} title='No template selected' color='gray'>
        Please select an email template from the dropdown above to preview and fill in the details.
      </Alert>
    )
  }

  const subjectLine = (subject: string) => {
    return (
      <Paper p='md' withBorder style={{ backgroundColor: 'white' }}>
        <Text size='xs' c='dimmed' mb='xs'>Subject:</Text>
        <Text size='sm' fw={500}>
          {subject}
        </Text>
      </Paper>
    )
  }

  return (
    <Stack gap='md'>
      <Paper p='md' withBorder>
        <Group justify='space-between' mb={isTemplateInfoExpanded ? 'md' : 0}>
          <Group gap='xs'>
            <Mail size={16} color='gray' />
            <Text size='sm' fw={600}>
              Template Information
            </Text>
          </Group>
          <Button
            variant='subtle'
            size='xs'
            onClick={() => setIsTemplateInfoExpanded(!isTemplateInfoExpanded)}
            rightSection={isTemplateInfoExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            p={4}
          >
            {isTemplateInfoExpanded ? 'Show Less' : 'Show More'}
          </Button>
        </Group>
        <Collapse expanded={isTemplateInfoExpanded}>
          <Text size='sm' c='dimmed'>
            Template: <Text span fw={500}>{template.name}</Text>
          </Text>
          <Text size='xs' c='dimmed' mt={4}>
            ID: {template.id}
          </Text>
        </Collapse>
      </Paper>

      <Paper p='md' withBorder>
        <Text size='sm' fw={600} mb='md'>
          Fill in Template Fields
        </Text>

        <Stack gap='md'>
          <TextInput
            label='Device Name'
            placeholder='Enter device name'
            value={formData.device_name}
            onChange={(e) => handleInputChange('device_name', e.currentTarget.value)}
            required
            error={errors.device_name && 'Device name is required'}
            size='sm'
            rightSection={
              formData.device_name.trim() ? (
                <CheckCircle2 size={18} color='green' />
              ) : undefined
            }
            styles={{
              label: {
                '& .mantineInputWrapperRequired': {
                  display: formData.device_name.trim() ? 'none' : 'inline',
                },
              },
            }}
          />

          <TextInput
            label='Contact Name'
            placeholder='Enter contact name'
            value={formData.contact_name}
            onChange={(e) => handleInputChange('contact_name', e.currentTarget.value)}
            required
            error={errors.contact_name && 'Contact name is required'}
            size='sm'
            rightSection={
              formData.contact_name.trim() ? (
                <CheckCircle2 size={18} color='green' />
              ) : undefined
            }
            styles={{
              label: {
                '& .mantineInputWrapperRequired': {
                  display: formData.contact_name.trim() ? 'none' : 'inline',
                },
              },
            }}
          />

          {/* Additional Info Section - Collapsible */}
          <Paper p='sm' withBorder style={{ backgroundColor: '#f8f9fa' }}>
            <Group
              gap={'xs'}
              mb={showAdditionalInfo ? 'md' : 0}
              style={{ cursor: 'pointer' }}
              onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
            >
              <ActionIcon
                size={'sm'}
                variant={'subtle'}
                color={'gray'}
              >
                {showAdditionalInfo ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </ActionIcon>
              <Text size={'sm'} fw={500}>
                Additional Information
              </Text>
              <Text size={'xs'} c={'dimmed'}>
                (Optional)
              </Text>
            </Group>

            {showAdditionalInfo && (
              <Stack gap='md'>
                <Textarea
                  label='Additional Info (Pre)'
                  placeholder='Optional additional information to display before the main content'
                  value={formData.additional_info_pre}
                  onChange={(e) => handleInputChange('additional_info_pre', e.currentTarget.value)}
                  autosize
                  minRows={2}
                  maxRows={4}
                  size='sm'
                />

                <Textarea
                  label='Additional Info (Post)'
                  placeholder='Optional additional information to display after the main content'
                  value={formData.additional_info_post}
                  onChange={(e) => handleInputChange('additional_info_post', e.currentTarget.value)}
                  autosize
                  minRows={2}
                  maxRows={4}
                  size='sm'
                />
              </Stack>
            )}
          </Paper>
        </Stack>
      </Paper>

      {isFormValid && renderedTemplate && (
        <Stack gap='xs'>
          <Group justify='space-between' align='center'>
            <Group gap='xs'>
              <Eye size={16} color='gray' />
              <Text size='sm' fw={600}>
                Template Preview
              </Text>
            </Group>
            <Group gap='xs'>
              <Button
                variant='subtle'
                size='xs'
                leftSection={<Maximize2 size={16} />}
                onClick={() => setIsFullscreen(true)}
              >
                Fullscreen
              </Button>
              <Button
                variant='subtle'
                size='xs'
                rightSection={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Hide Preview' : 'Show Preview'}
              </Button>
            </Group>
          </Group>

          <Collapse expanded={isExpanded}>
            <Paper p='md' withBorder style={{ backgroundColor: '#f8f9fa' }}>
              <Tabs defaultValue='plain'>
                <Tabs.List>
                  <Tabs.Tab value='plain'>Plain Text</Tabs.Tab>
                  {/*<Tabs.Tab value='subject'>Subject Line</Tabs.Tab>*/}
                  <Tabs.Tab value='html'>HTML Preview</Tabs.Tab>
                </Tabs.List>

                {/*<Tabs.Panel value='subject' pt='md'>*/}
                {/*  {subjectLine(renderedTemplate.subject)}*/}
                {/*</Tabs.Panel>*/}

                <Tabs.Panel value='plain' pt='md'>
                  {subjectLine(renderedTemplate.subject)}
                  <Paper p='md' withBorder style={{ backgroundColor: 'white' }}>
                    <Text size='xs' c='dimmed' mb='xs'>Email Body:</Text>
                    <Text
                      size='sm'
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        lineHeight: 1.6,
                      }}
                    >
                      {renderedTemplate.plainContent}
                    </Text>
                  </Paper>
                </Tabs.Panel>

                <Tabs.Panel value='html' pt='md'>
                  {subjectLine(renderedTemplate.subject)}
                  <Paper withBorder style={{ backgroundColor: 'white' }}>
                    {/*<Text size='xs' c='dimmed' p='md' pb='xs'>Rendered HTML Email:</Text>*/}
                    <div style={{
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                    }}>
                      <iframe
                        srcDoc={renderedTemplate.htmlContent}
                        style={{
                          width: '100%',
                          minHeight: '500px',
                          border: 'none',
                          display: 'block',
                        }}
                        title='Email HTML Preview'
                      />
                    </div>
                  </Paper>
                </Tabs.Panel>
              </Tabs>
            </Paper>
          </Collapse>
        </Stack>
      )}

      {!isFormValid && (
        <Alert icon={<AlertCircle size={16} />} title='Required fields missing' color='yellow'>
          Please fill in all required fields (Device Name and Contact Name) to see the preview.
        </Alert>
      )}

      {/* Fullscreen Modal */}
      <Modal
        opened={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title={
          <Group gap='xs'>
            <Eye size={18} />
            <Text fw={600}>Template Preview - {template.name}</Text>
          </Group>
        }
        size='xl'
        fullScreen
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <Tabs defaultValue='plain'>
          <Tabs.List>
            <Tabs.Tab value='plain'>Plain Text</Tabs.Tab>
            <Tabs.Tab value='html'>HTML Preview</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='plain' pt='md'>
            {renderedTemplate && subjectLine(renderedTemplate.subject)}
            {renderedTemplate && (
              <Paper p='md' withBorder style={{ backgroundColor: 'white' }} mt='md'>
                <Text size='xs' c='dimmed' mb='xs'>Email Body:</Text>
                <Text
                  size='sm'
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    lineHeight: 1.6,
                  }}
                >
                  {renderedTemplate.plainContent}
                </Text>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value='html' pt='md'>
            {renderedTemplate && subjectLine(renderedTemplate.subject)}
            {renderedTemplate && (
              <Paper withBorder style={{ backgroundColor: 'white' }} mt='md'>
                <div style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                }}>
                  <iframe
                    srcDoc={renderedTemplate.htmlContent}
                    style={{
                      width: '100%',
                      height: 'calc(100vh - 200px)',
                      border: 'none',
                      display: 'block',
                    }}
                    title='Email HTML Preview Fullscreen'
                  />
                </div>
              </Paper>
            )}
          </Tabs.Panel>
        </Tabs>
      </Modal>
    </Stack>
  )
}
